import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { getLoginUrl } from "@/const";
import { CDN, INDUSTRY_LABELS } from "@shared/constants";
import { Link } from "wouter";
import { useState, useEffect, lazy, Suspense } from "react";
import {
  Target, MessageSquare, BarChart3, Users, Trophy, Zap,
  ChevronDown, ChevronUp, ArrowRight, Play, Shield, Clock,
  CheckCircle2, Menu, X
} from "lucide-react";
import { motion } from "framer-motion";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

// A/B test: hero CTA variant (persisted per visitor)
function getHeroVariant(): "A" | "B" {
  try {
    const stored = localStorage.getItem("closerai_hero_variant");
    if (stored === "A" || stored === "B") return stored;
    const variant = Math.random() < 0.5 ? "A" : "B";
    localStorage.setItem("closerai_hero_variant", variant);
    return variant;
  } catch { return "A"; }
}

// Track A/B test CTA clicks via Umami custom events
function trackHeroClick(variant: "A" | "B", button: "primary" | "secondary") {
  try {
    const w = window as any;
    if (w.umami) {
      w.umami.track(`hero_cta_click`, { variant, button });
    }
  } catch {}
}

/* ─── 1. NAVBAR ─────────────────────────────────────────────── */
function Navbar() {
  const { isAuthenticated } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-[#0A0E1A]/80 backdrop-blur-xl">
      <div className="container flex items-center justify-between h-16">
        <Link href="/" className="flex items-center gap-2">
          <img src={CDN.logo} alt="CloserAI" className="h-8 w-8 object-contain" />
          <span className="text-xl font-bold text-foreground">CloserAI</span>
        </Link>
        <div className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Features</a>
          <a href="#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Pricing</a>
          <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Blog</Link>
          <Link href="/resources" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Resources</Link>
          {isAuthenticated ? (
            <Link href="/dashboard">
              <Button size="sm" className="bg-primary text-primary-foreground hover:bg-[#33DDFF] glow-cyan">Dashboard</Button>
            </Link>
          ) : (
            <div className="flex items-center gap-3">
              <a href={getLoginUrl()}>
                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">Log In</Button>
              </a>
              <a href={getLoginUrl("/dashboard")}>
                <Button size="sm" className="bg-primary text-primary-foreground hover:bg-[#33DDFF] glow-cyan">
                  Start Free <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </a>
            </div>
          )}
        </div>
        <button className="md:hidden text-foreground" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle navigation menu">
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {mobileOpen && (
        <div className="md:hidden border-t border-white/10 bg-[#0A0E1A]/95 backdrop-blur-xl p-4 space-y-3">
          <a href="#features" className="block text-sm text-muted-foreground" onClick={() => setMobileOpen(false)}>Features</a>
          <a href="#pricing" className="block text-sm text-muted-foreground" onClick={() => setMobileOpen(false)}>Pricing</a>
          <Link href="/blog" className="block text-sm text-muted-foreground" onClick={() => setMobileOpen(false)}>Blog</Link>
          <Link href="/resources" className="block text-sm text-muted-foreground" onClick={() => setMobileOpen(false)}>Resources</Link>
          {isAuthenticated ? (
            <Link href="/dashboard"><Button className="w-full bg-primary text-primary-foreground">Dashboard</Button></Link>
          ) : (
            <a href={getLoginUrl("/dashboard")}><Button className="w-full bg-primary text-primary-foreground">Start Free</Button></a>
          )}
        </div>
      )}
    </nav>
  );
}

/* ─── 2. HERO ──────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="relative pt-32 pb-20 md:pt-44 md:pb-32 overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${CDN.heroBg})` }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0E1A]/60 via-[#0A0E1A]/80 to-[#0A0E1A]" />
      <div className="container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card text-sm text-primary">
              <Zap className="h-4 w-4" /><span>AI-Powered Sales Training</span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1.1]">
              <span className="text-foreground">Practice Your Sales Pitch on AI Buyers.</span><br />
              <span className="gradient-text">Get Scored. Get Better.</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-lg leading-relaxed">
              The flight simulator for salespeople. One free practice session — no sign-up required.
              Pick a scenario, sell to an AI buyer, get scored across 6 dimensions.
            </p>
            {/* Primary CTA — unmissable */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link href="/guest-practice">
                <Button size="lg" className="bg-[#22C55E] text-white hover:bg-[#16A34A] text-xl px-10 py-7 shadow-lg shadow-[#22C55E]/25 font-bold">
                  Practice Free →
                </Button>
              </Link>
              <a href="#pricing">
                <Button size="lg" variant="outline" className="border-white/20 text-foreground hover:bg-white/5 text-lg px-8 py-7">
                  See Pricing
                </Button>
              </a>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-[#22C55E]" /> 1 free session, no account needed</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-[#22C55E]" /> No credit card required</span>
            </div>
            {/* Inline pricing strip */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <span className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-sm">
                <span className="font-bold text-foreground">Free</span> <span className="text-muted-foreground">$0</span>
              </span>
              <span className="px-4 py-2 rounded-lg bg-primary/10 border border-primary/30 text-sm">
                <span className="font-bold text-foreground">Pro</span> <span className="text-muted-foreground">$29/mo</span>
              </span>
              <span className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-sm">
                <span className="font-bold text-foreground">Team</span> <span className="text-muted-foreground">$49/user/mo</span>
              </span>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute -inset-4 bg-primary/10 rounded-2xl blur-3xl" />
              <img src={CDN.chatPreview} alt="CloserAI roleplay interface" loading="lazy" className="relative rounded-xl border border-white/10 shadow-2xl" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── 2b. VIDEO DEMO SECTION ──────────────────────────────── */
function DemoSection() {
  return (
    <section id="demo" className="overflow-hidden bg-[#111827]/30 py-20 md:py-28">
      <div className="container">
        <div className="grid items-center gap-12 lg:grid-cols-[0.72fr_1.28fr]">
          <div>
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.22em] text-primary">Meet Molly · 93-second demo</p>
            <h2 className="text-3xl font-bold text-foreground md:text-5xl">See how practice becomes progress.</h2>
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
              Molly walks through the complete CloserAI experience: realistic AI buyers, six-dimension coaching, transparent plans, and one full roleplay before signup.
            </p>
            <div className="mt-7 space-y-3 text-sm text-muted-foreground">
              <p className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#22C55E]" aria-hidden="true" /> Three real product stages explained in plain language</p>
              <p className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#22C55E]" aria-hidden="true" /> Exact Free, Pro, and Team pricing</p>
              <p className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#22C55E]" aria-hidden="true" /> AI provides practice and feedback; you make the decisions</p>
            </div>
            <Link href="/guest-practice">
              <Button className="mt-8 bg-[#22C55E] px-7 py-6 font-bold text-white hover:bg-[#16A34A]">Practice Free <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </Link>
          </div>

          <div className="relative">
            <div className="absolute -inset-8 rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-2xl border border-primary/20 bg-[#050b14] shadow-2xl shadow-black/40">
              <video
                className="aspect-video w-full bg-black object-cover"
                src="/manus-storage/closeraiapp.com - Molly Demo_fd2d61b8.mp4"
                poster="/manus-storage/molly-demo-poster_791e1021.jpg"
                autoPlay
                muted
                loop
                controls
                playsInline
                preload="metadata"
                aria-label="Molly demonstrates CloserAI sales roleplay training, scoring, and pricing"
              >
                Your browser does not support embedded video. You can still <Link href="/guest-practice">try CloserAI free</Link>.
              </video>
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-white/10 px-4 py-3 text-xs text-muted-foreground">
                <span>closeraiapp.com — Molly Demo</span>
                <span>Autoplay is muted · Full controls available</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── 3. SOCIAL PROOF BAR ─────────────────────────────────── */function SocialProofBar() {
  const stats = [
    { value: "5", label: "Sales Industries" },
    { value: "15", label: "Built-In Scenarios" },
    { value: "6", label: "Scoring Dimensions" },
    { value: "1", label: "Free Guest Roleplay" },
  ];
  return (
    <section className="py-12 border-y border-white/5 bg-[#111827]/50">
      <div className="container">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-3xl md:text-4xl font-bold gradient-text" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{s.value}</div>
              <div className="text-sm text-muted-foreground mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 4. HOW IT WORKS ─────────────────────────────────────── */
function HowItWorks() {
  const steps = [
    { number: "01", title: "Choose a Scenario", description: "Pick from 15 built-in scenarios across SaaS, Real Estate, Insurance, Retail, and Automotive. Each has a unique buyer persona and difficulty level.", icon: Target },
    { number: "02", title: "Practice with AI", description: "Engage in a realistic sales conversation. The AI buyer persona pushes back with objections, plays hard to get, and tests your closing skills.", icon: MessageSquare },
    { number: "03", title: "Get Scored & Improve", description: "Receive detailed scores across 6 dimensions — Rapport, Discovery, Objection Handling, Value Prop, Closing, and Professionalism — with actionable coaching tips.", icon: BarChart3 },
  ];
  return (
    <section id="how-it-works" className="py-20 md:py-28">
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">How It Works</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">Three simple steps to becoming a better closer. Practice anytime, anywhere.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step) => (
            <motion.div key={step.number} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: parseInt(step.number) * 0.15 }} className="relative glass-card rounded-xl p-8 hover:border-primary/30 transition-all duration-300 group">
              <div className="text-6xl font-bold text-white/5 absolute top-4 right-6" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{step.number}</div>
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                <step.icon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">{step.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 5. PERSONA SHOWCASE ─────────────────────────────────── */
function PersonaShowcase() {
  const personas = [
    { name: "Marcus Chen", title: "Skeptical CFO", difficulty: "Advanced", image: CDN.personaCfo, trait: "Analytical, budget-focused, needs hard ROI data" },
    { name: "Sarah Mitchell", title: "Busy Executive", difficulty: "Intermediate", image: CDN.personaExecutive, trait: "Time-pressed, direct, values efficiency" },
    { name: "David Park", title: "Price Shopper", difficulty: "Beginner", image: CDN.personaShopper, trait: "Comparison-driven, seeks best deal, price-sensitive" },
    { name: "Linda Torres", title: "Gatekeeper", difficulty: "Advanced", image: CDN.personaGatekeeper, trait: "Protective, skeptical of outsiders, guards access" },
    { name: "James Wilson", title: "Internal Champion", difficulty: "Beginner", image: CDN.personaChampion, trait: "Supportive but needs ammunition to sell internally" },
  ];
  const diffColor: Record<string, string> = { Beginner: "text-[#22C55E]", Intermediate: "text-[#F59E0B]", Advanced: "text-[#EF4444]" };
  return (
    <section className="py-20 md:py-28 bg-[#111827]/30">
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Meet Your AI Buyer Personas</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">Five distinct personalities that will challenge your sales skills from every angle.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {personas.map((p) => (
            <motion.div key={p.name} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="glass-card rounded-xl p-5 text-center hover:border-primary/30 transition-all duration-300 group">
              <div className="relative mx-auto w-20 h-20 mb-4">
                <div className="absolute inset-0 rounded-full bg-primary/20 blur-md group-hover:bg-primary/30 transition-colors" />
                <img src={p.image} alt={p.name} loading="lazy" className="relative w-20 h-20 rounded-full object-cover border-2 border-white/10" />
              </div>
              <h3 className="font-semibold text-foreground text-sm">{p.name}</h3>
              <p className="text-primary text-xs font-medium mt-1">{p.title}</p>
              <p className={`text-xs mt-1 ${diffColor[p.difficulty]}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{p.difficulty}</p>
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{p.trait}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 6. FEATURES GRID ────────────────────────────────────── */
function FeaturesGrid() {
  const features = [
    { icon: MessageSquare, title: "AI Roleplays", description: "Practice with realistic AI buyer personas that adapt to your approach and push back with real objections." },
    { icon: BarChart3, title: "6-Dimension Scoring", description: "Get scored on Rapport, Discovery, Objection Handling, Value Prop, Closing, and Professionalism after every session." },
    { icon: Target, title: "Industry Scenarios", description: "15 built-in scenarios across SaaS, Real Estate, Insurance, Retail, and Automotive — with more coming." },
    { icon: Clock, title: "Session History", description: "Track every practice session with full transcripts, scores, and AI coaching feedback to measure your progress." },
    { icon: Trophy, title: "Leaderboards", description: "Compete with other sales professionals globally. See where you rank by industry, period, and score." },
    { icon: Users, title: "Team Analytics", description: "Managers can track team performance, compare reps, and identify coaching opportunities across the team." },
  ];
  return (
    <section id="features" className="py-20 md:py-28">
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Everything You Need to Close More Deals</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">A complete sales training platform designed for individual reps and teams alike.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass-card rounded-xl p-6 hover:border-primary/30 transition-all duration-300 group">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <f.icon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">{f.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{f.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 7. CHAT PREVIEW ─────────────────────────────────────── */
function ChatPreview() {
  return (
    <section className="py-20 md:py-28 bg-[#111827]/30">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              A Roleplay Interface Built for <span className="gradient-text">Real Practice</span>
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Our immersive chat interface puts you in the driver's seat. A focused timer guides the practice,
              then detailed scoring and coaching feedback arrive when the conversation ends.
            </p>
            <ul className="space-y-3">
              {["Focused roleplay timer during the conversation", "AI personas that respond to your selling style", "Full session transcripts for review", "Actionable coaching feedback after every session"].map((item) => (
                <li key={item} className="flex items-start gap-3 text-muted-foreground">
                  <CheckCircle2 className="h-5 w-5 text-[#22C55E] mt-0.5 shrink-0" /><span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-primary/5 rounded-2xl blur-3xl" />
            <img src={CDN.dashboardPreview} alt="CloserAI dashboard" loading="lazy" className="relative rounded-xl border border-white/10 shadow-2xl" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── 8. PRICING ──────────────────────────────────────────── */
function PricingSection() {
  const plans = [
    { name: "Free", price: "$0", period: "forever", description: "Get started with sales practice", features: ["3 sessions per month", "1 industry (your choice)", "Basic scoring", "Session history"], cta: "Practice Free", href: "/guest-practice", isInternal: true, popular: false },
    { name: "Pro", price: "$29", period: "/month", description: "Unlimited practice for serious closers", features: ["Unlimited sessions", "All 5 industries", "Detailed analytics", "Full session history", "AI coaching tips", "Daily sales tips"], cta: "Go Pro", href: getLoginUrl("/upgrade"), isInternal: false, popular: true },
    { name: "Team", price: "$49", period: "/user/month", description: "Train your entire sales team", features: ["Everything in Pro", "Team management", "Team invitations", "Manager dashboard", "Rep performance visibility", "Leaderboards"], cta: "Choose Team", href: getLoginUrl("/upgrade"), isInternal: false, popular: false },
  ];
  return (
    <section id="pricing" className="py-20 md:py-28">
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Simple, Transparent Pricing</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">No "contact sales" wall. No hidden fees. Start free and upgrade when you're ready.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <motion.div key={plan.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className={`relative glass-card rounded-xl p-8 transition-all duration-300 ${plan.popular ? "border-primary/50 glow-cyan" : "hover:border-white/20"}`}>
              {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full">Most Popular</div>}
              <h3 className="text-xl font-bold text-foreground">{plan.name}</h3>
              <p className="text-muted-foreground text-sm mt-1">{plan.description}</p>
              <div className="mt-6 mb-6">
                <span className="text-4xl font-bold text-foreground" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{plan.price}</span>
                <span className="text-muted-foreground text-sm">{plan.period}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" /><span>{f}</span>
                  </li>
                ))}
              </ul>
              {plan.isInternal ? (
                <Link href={plan.href}>
                  <Button className="w-full bg-[#22C55E] text-white hover:bg-[#16A34A] font-bold">
                    {plan.cta} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              ) : (
                <a href={plan.href}>
                  <Button className={`w-full ${plan.popular ? "bg-primary text-primary-foreground hover:bg-[#33DDFF] glow-cyan" : "bg-white/5 text-foreground hover:bg-white/10 border border-white/10"}`}>
                    {plan.cta} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 9. FAQ ──────────────────────────────────────────────── */
function FAQ() {
  const faqs = [
    { q: "What is GetCloser AI and how does it work?", a: "GetCloser AI is an AI-powered sales training platform that lets you practice realistic sales conversations with AI buyer personas. You choose a scenario and industry, have a live conversation with an AI prospect, then receive feedback across 6 dimensions: rapport, discovery, objection handling, value proposition, closing, and professionalism." },
    { q: "How much does GetCloser AI cost?", a: "GetCloser AI offers three plans: a Free tier with 3 practice sessions per month, a Pro plan at $29/month with unlimited sessions across all 5 industries and detailed analytics, and a Team plan at $49/month per user that adds manager dashboards and rep comparison tools." },
    { q: "What industries and scenarios does GetCloser AI support?", a: "GetCloser AI covers 5 industry verticals: SaaS/Technology, Real Estate, Insurance, Retail/E-commerce, and Automotive. The library includes 15 scenarios across beginner, intermediate, and advanced difficulty levels." },
    { q: "How realistic are the AI buyer personas?", a: "Very. Each persona has a distinct personality, communication style, and set of objections based on real buyer archetypes. They adapt to your approach — if you build rapport well, they warm up. If you push too hard, they push back harder." },
    { q: "Can I use this for my sales team?", a: "Yes. The Team plan includes team management, a manager view, rep performance visibility, and leaderboards so managers can guide practice without taking judgment away from the coach or salesperson." },
    { q: "Is there a free plan?", a: "Yes! The Free plan includes 3 sessions per month, 1 industry, basic scoring, and full session history. No credit card required. Upgrade to Pro or Team anytime." },
    { q: "Does CloserAI make decisions for me?", a: "No. CloserAI simulates practice conversations and provides algorithmic scoring and coaching feedback. It may make mistakes and does not replace a salesperson's or manager's judgment. People remain responsible for every real-world sales decision." },
  ];
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return (
    <section id="faq" className="py-20 md:py-28 bg-[#111827]/30">
      <div className="container max-w-3xl">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="glass-card rounded-xl overflow-hidden transition-all duration-300">
              <button className="w-full flex items-center justify-between p-5 text-left" onClick={() => setOpenIndex(openIndex === i ? null : i)}>
                <span className="text-foreground font-medium pr-4">{faq.q}</span>
                {openIndex === i ? <ChevronUp className="h-5 w-5 text-primary shrink-0" /> : <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />}
              </button>
              {openIndex === i && <div className="px-5 pb-5 text-muted-foreground leading-relaxed">{faq.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 10. CTA FOOTER ──────────────────────────────────────── */
function CTAFooter() {
  return (
    <section className="py-20 md:py-28">
      <div className="container">
        <div className="relative glass-card rounded-2xl p-12 md:p-16 text-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-[#8B5CF6]/5" />
          <div className="relative z-10">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">Ready to Close More Deals?</h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto mb-8">
              Build confidence before your next real conversation. Practice one complete roleplay for free, with no signup required.
            </p>
            <Link href="/guest-practice">
              <Button size="lg" className="bg-[#22C55E] text-white hover:bg-[#16A34A] text-lg px-10 py-6 font-bold shadow-lg shadow-[#22C55E]/25">
                Practice Free →
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── 10A. HUMAN CONTACT ───────────────────────────────────── */
type ContactTopic = "general" | "team_demo" | "pricing" | "technical";

function LeadCaptureForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState<ContactTopic>("general");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const submitContact = trpc.leadCapture.submit.useMutation({
    onSuccess: () => {
      setSubmitted(true);
      setName("");
      setEmail("");
      setTopic("general");
      setMessage("");
      setWebsite("");
    },
    onError: error => toast.error(error.message || "We could not send your message. Please try again."),
  });

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    submitContact.mutate({ name, email, topic, message, website });
  }

  return (
    <section id="contact" className="py-20 md:py-28" aria-labelledby="contact-heading">
      <div className="container">
        <div className="mx-auto grid max-w-5xl gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
          <div className="lg:sticky lg:top-28">
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.22em] text-primary">A human response</p>
            <h2 id="contact-heading" className="text-3xl font-bold text-foreground md:text-5xl">
              Questions? Let us talk.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
              Ask about a team demo, pricing, account access, or anything that would help you decide whether CloserAI fits your sales team.
            </p>
            <div className="mt-8 space-y-4 text-sm text-muted-foreground">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#22C55E]" aria-hidden="true" />
                <p>Your message is preserved and sent directly to the owner.</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#22C55E]" aria-hidden="true" />
                <p>No sales list, no spam, and no payment information collected.</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#22C55E]" aria-hidden="true" />
                <p>AI helps with training; a person handles your inquiry.</p>
              </div>
            </div>
          </div>

          <div className="glass-card rounded-2xl border border-white/10 p-6 md:p-9">
            {submitted ? (
              <div className="flex min-h-[420px] flex-col items-center justify-center text-center" role="status">
                <span className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#22C55E]/15">
                  <CheckCircle2 className="h-8 w-8 text-[#22C55E]" aria-hidden="true" />
                </span>
                <h3 className="text-2xl font-bold text-foreground">Your message is on its way.</h3>
                <p className="mt-3 max-w-md leading-relaxed text-muted-foreground">
                  Thank you. We sent a confirmation to your email and will follow up with a human response.
                </p>
                <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                  <Link href="/guest-practice">
                    <Button className="bg-[#22C55E] text-white hover:bg-[#16A34A]">Practice Free</Button>
                  </Link>
                  <Button variant="outline" onClick={() => setSubmitted(false)}>Send Another Message</Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mb-2 block text-sm font-medium text-foreground">Name</label>
                    <input
                      id="contact-name"
                      type="text"
                      autoComplete="name"
                      value={name}
                      onChange={event => setName(event.target.value)}
                      minLength={2}
                      maxLength={100}
                      required
                      placeholder="Your name"
                      className="w-full rounded-lg border border-border bg-background px-4 py-3 text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/25"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="mb-2 block text-sm font-medium text-foreground">Work email</label>
                    <input
                      id="contact-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={event => setEmail(event.target.value)}
                      maxLength={320}
                      required
                      placeholder="you@company.com"
                      className="w-full rounded-lg border border-border bg-background px-4 py-3 text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/25"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-topic" className="mb-2 block text-sm font-medium text-foreground">What can we help with?</label>
                  <select
                    id="contact-topic"
                    value={topic}
                    onChange={event => setTopic(event.target.value as ContactTopic)}
                    className="w-full rounded-lg border border-border bg-background px-4 py-3 text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/25"
                  >
                    <option value="general">General question</option>
                    <option value="team_demo">Team demo</option>
                    <option value="pricing">Pricing question</option>
                    <option value="technical">Technical question</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className="mb-2 block text-sm font-medium text-foreground">Message</label>
                  <textarea
                    id="contact-message"
                    value={message}
                    onChange={event => setMessage(event.target.value)}
                    minLength={10}
                    maxLength={1500}
                    rows={5}
                    required
                    placeholder="Tell us what you want to accomplish..."
                    className="w-full resize-y rounded-lg border border-border bg-background px-4 py-3 text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/25"
                  />
                  <p className="mt-2 text-right text-xs text-muted-foreground">{message.length}/1500</p>
                </div>

                <div className="absolute -left-[10000px]" aria-hidden="true">
                  <label htmlFor="contact-website">Website</label>
                  <input
                    id="contact-website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={event => setWebsite(event.target.value)}
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={submitContact.isPending}
                  className="w-full bg-primary py-6 font-semibold text-primary-foreground hover:bg-primary/90"
                >
                  {submitContact.isPending ? "Sending..." : "Send My Message"}
                </Button>
                <p className="text-center text-xs leading-relaxed text-muted-foreground">
                  By sending this form, you agree to our <Link href="/privacy" className="text-primary hover:underline">Privacy Policy</Link>. We use your details only to respond to your inquiry.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── 11. FOOTER ──────────────────────────────────────────── */
function Footer() {
  return (
    <footer className="py-12 border-t border-white/5">
      <div className="container">
        <div className="grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <img src={CDN.logo} alt="CloserAI" className="h-8 w-8 object-contain" />
              <span className="text-lg font-bold text-foreground">CloserAI</span>
            </div>
            <p className="text-muted-foreground text-sm max-w-sm leading-relaxed">
              The flight simulator for salespeople. Practice real sales conversations with AI, get scored, and close more deals.
            </p>
            <p className="text-muted-foreground text-sm mt-3">
              Contact: <a href="mailto:support@closeraiapp.com" className="text-primary hover:underline">support@closeraiapp.com</a>
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">Product</h3>
            <ul className="space-y-2">
              <li><a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Features</a></li>
              <li><a href="#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Pricing</a></li>
              <li><a href="#faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors">FAQ</a></li>
            </ul>
            <h3 className="text-sm font-semibold text-foreground mb-4 mt-6">Resources</h3>
            <ul className="space-y-2">
              <li><Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Blog</Link></li>
              <li><Link href="/resources" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Free Downloads</Link></li>
              <li><Link href="/testimonials" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Testimonials</Link></li>
              <li><Link href="/referral" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Referral Program</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">Legal</h3>
            <ul className="space-y-2">
              <li><Link href="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link></li>
              <li><Link href="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Terms of Service</Link></li>
            </ul>
            <h3 className="text-sm font-semibold text-foreground mb-4 mt-6">Industries</h3>
            <ul className="space-y-2">
              {Object.values(INDUSTRY_LABELS).map((label) => (
                <li key={label}><span className="text-sm text-muted-foreground">{label}</span></li>
              ))}
            </ul>
          </div>
        </div>
        {/* More from our network */}
        <div className="mt-10 pt-8 border-t border-white/5">
          <h3 className="text-sm font-semibold text-foreground mb-3">More from our network</h3>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <a href="https://caterdrop.io" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-foreground transition-colors">CaterDrop — AI Catering Marketplace</a>
            <a href="https://richyryan.com" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Richy Ryan — Editorial Platform</a>
            <a href="https://closeraiapp.com" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-foreground transition-colors">CloserAI — Sales Training App</a>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left">
            <p className="text-sm text-muted-foreground">&copy; {new Date().getFullYear()} CloserAI. All rights reserved.</p>
            <p className="mt-1 text-xs text-muted-foreground">AI-assisted practice and feedback. Humans make the decisions.</p>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <div className="flex items-center gap-1">
              <Shield className="h-4 w-4" /><span>Encrypted & Secure</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─── MAIN PAGE ───────────────────────────────────────────── */
export default function Home() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <Hero />
      <DemoSection />
      <SocialProofBar />
      <HowItWorks />
      <PersonaShowcase />
      <FeaturesGrid />
      <ChatPreview />
      <PricingSection />
      <FAQ />
      <CTAFooter />
      <LeadCaptureForm />
      <Footer />
    </div>
  );
}
